import { observer } from 'mobx-react-lite';
import {
    ContextualMenu,
    ContextualMenuItemType,
    DirectionalHint,
    IContextualMenuItem,
} from '@fluentui/react/lib/ContextualMenu';
import { logUsage } from 'owa-analytics';
import {
    AttachmentSuggestionsMenuItems,
    lazyGetAttachmentSuggestionsMenuItems,
    ReplyWithAttachmentSuggestion,
} from 'owa-attachment-suggestions';
import { ControlIcons } from 'owa-control-icons';
import { OwaDate, owaDate } from 'owa-datetime';
import { EditorViewState } from 'owa-editor';
import { InsertLinkSource } from 'owa-editor-ribbonplugin';
import fixupHotkeyCommand from 'owa-hotkeys/lib/utils/fixupHotkeyCommand';
import { useInjected } from 'owa-inject';
import { format } from 'owa-localize';
import { convertHotkeyStringToReadableText } from 'owa-readable-hotkeys/lib/utils/convertHotkeyStringToReadableText';
import * as React from 'react';
import { InsertLinkFromFilePickerContext } from '../types/InsertLinkFromFilePickerContext';
import { getAttachmentCommandItems } from '../utils/getAttachmentCommandItems';
import { onSuggestionAttachmentClicked } from '../utils/onSuggestionAttachmentClicked';

export interface HyperLinkContextMenuProps {
    menuTargetElement: HTMLElement;
    onDismissMenu: () => void;
    filePickerContext: InsertLinkFromFilePickerContext;
    editorViewState: EditorViewState;
    targetWindow: Window;
    insertLinkTitle: string;
    insertLinkShortcut: string;
    showInsertLinkDialog: (
        editorViewState: EditorViewState,
        targetWindow: Window,
        source: InsertLinkSource,
        startTime: OwaDate,
        onDismissDialog?: () => void
    ) => void;
}

const styles = require('./HyperlinkContextMenu.scss');
const classNames = require('classnames');

export const HyperlinkContextMenu = observer(function HyperlinkContextMenu(
    contextMenuProps: HyperLinkContextMenuProps
) {
    const attachmentSuggestionsMenuItems = React.useRef<AttachmentSuggestionsMenuItems>();
    if (!attachmentSuggestionsMenuItems.current) {
        attachmentSuggestionsMenuItems.current = lazyGetAttachmentSuggestionsMenuItems.tryImportForRender()?.(
            true /* isMail */
        );
    }

    const startTime = owaDate();
    const contextMenuItems: IContextualMenuItem[] = [];
    const attachmentMenuItems: IContextualMenuItem[] = getAttachmentCommandItems(
        contextMenuProps.filePickerContext,
        contextMenuProps.editorViewState,
        contextMenuProps.targetWindow,
        contextMenuProps.filePickerContext.sxsId
    );
    const [convertHotkeyStringToReadableTextInjected] = useInjected(
        convertHotkeyStringToReadableText
    );
    const insertLinkTitle = format(
        contextMenuProps.insertLinkTitle,
        convertHotkeyStringToReadableTextInjected(
            // cast back to string because fixupHotkeyCommand returns the same type it was passed
            fixupHotkeyCommand(contextMenuProps.insertLinkShortcut) as string
        )
    );

    const attachmentSuggestions = attachmentSuggestionsMenuItems.current?.getContextualMenuItems(
        true /* shouldUseSuggestions */,
        contextMenuProps.filePickerContext.referenceItemId,
        true /* referenceAttachmentsSupported */,
        false /* classicAttachmentsSupported */,
        true /* shouldUseSuggetedFilesTitle */,
        (suggestion: ReplyWithAttachmentSuggestion, suggestionIndex: number) => {
            onSuggestionAttachmentClicked(
                suggestion,
                contextMenuProps.filePickerContext.insertLinksHost,
                contextMenuProps.editorViewState,
                contextMenuProps.filePickerContext.fromAddress,
                contextMenuProps.targetWindow,
                contextMenuProps.filePickerContext.sxsId
            );
        }
    );
    if (attachmentSuggestions) {
        contextMenuItems.push(...attachmentSuggestions);
        contextMenuItems.push(getDividerForContextMenu());
    }

    contextMenuItems.push(...attachmentMenuItems);
    contextMenuItems.push(
        getInsertLinkMenuItem(
            contextMenuProps.editorViewState,
            contextMenuProps.targetWindow,
            insertLinkTitle,
            contextMenuProps.showInsertLinkDialog,
            startTime
        )
    );

    return (
        <ContextualMenu
            target={contextMenuProps.menuTargetElement}
            shouldFocusOnMount={true}
            onDismiss={contextMenuProps.onDismissMenu}
            items={contextMenuItems}
            directionalHint={DirectionalHint.topLeftEdge}
            gapSpace={2}
        />
    );
});

const getInsertLinkMenuItem = (
    editorViewState: EditorViewState,
    targetWindow: Window,
    insertLinkTitle: string,
    showInsertLinkDialog: (
        editorViewState: EditorViewState,
        targetWindow: Window,
        source: InsertLinkSource,
        startTime: OwaDate,
        onDismissDialog?: () => void
    ) => void,
    startTime: OwaDate
): IContextualMenuItem => {
    return {
        key: 'insertLinkContextMenuItem',
        name: insertLinkTitle,
        title: insertLinkTitle,
        iconProps: {
            iconName: ControlIcons.Link,
            styles: { root: classNames(styles.iconRoot, styles.iconPadding) },
        },
        onClick: () => {
            logUsage('HyperlinkInsertLinkDialog');
            showInsertLinkDialog(
                editorViewState,
                targetWindow,
                InsertLinkSource.FilePickerLinkContextMenu,
                startTime
            );
        },
    };
};

function getDividerForContextMenu(): IContextualMenuItem {
    return {
        key: 'HyperlinkContextMenuDivider',
        className: styles.separator,
        itemType: ContextualMenuItemType.Divider,
    };
}
